/**
 * Pixels desta página — os que existem além da medição da conta.
 *
 * A tela deixa claro o que já está ligado globalmente antes de oferecer o
 * botão de adicionar. Sem isso, a leitura natural de uma lista vazia é "esta
 * página não mede nada", e o operador cadastra o GA4 de novo aqui — passando a
 * contar cada visita duas vezes num relatório que ninguém confere.
 */

import {
  PROVEDORES_PIXEL,
  ROTULOS_PIXEL,
  erroDeFormato,
  type Pixel,
  type ProvedorPixel,
} from '@egba-do/shared'

import { Aviso, Botao, Campo, Selecao } from '../ui'

export interface PixelsDaPaginaProps {
  pixels: Pixel[]
  /** O que a configuração da conta já aplica em toda página. */
  daConta: Pixel[]
  onAlterar: (pixels: Pixel[]) => void
}

export function PixelsDaPagina({ pixels, daConta, onAlterar }: PixelsDaPaginaProps) {
  function alterar(i: number, mudanca: Partial<Pixel>): void {
    onAlterar(pixels.map((p, j) => (i === j ? { ...p, ...mudanca } : p)))
  }

  return (
    <div className="space-y-3">
      {daConta.length > 0 ? (
        <Aviso tom="info" titulo="Já ativos em todas as landing pages">
          <ul className="mt-1 space-y-0.5">
            {daConta.map((p) => (
              <li key={`${p.provedor}:${p.id}`}>
                {ROTULOS_PIXEL[p.provedor].nome} · <span className="font-mono">{p.id}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2">
            Vêm de <strong>Configurações → Medição e remarketing</strong>. Não repita aqui — o
            mesmo pixel duas vezes conta cada visita duas vezes.
          </p>
        </Aviso>
      ) : (
        <Aviso tom="atencao">
          Nenhuma medição configurada para a conta. Cadastre GA4, Meta e afins em{' '}
          <strong>Configurações → Medição e remarketing</strong> para valer em todas as páginas de
          uma vez.
        </Aviso>
      )}

      {pixels.length > 0 && (
        <ul className="space-y-3">
          {pixels.map((p, i) => {
            const erro = erroDeFormato(p.provedor, p.id)
            return (
              <li
                key={i}
                className="space-y-3 rounded-lg border border-[color:var(--color-borda)] p-3"
              >
                <div className="grid grid-cols-2 gap-3">
                  <Selecao
                    rotulo="Provedor"
                    value={p.provedor}
                    onChange={(e) =>
                      alterar(i, { provedor: e.target.value as ProvedorPixel, rotuloConversao: undefined })
                    }
                    opcoes={PROVEDORES_PIXEL.map((v) => ({
                      valor: v,
                      texto: ROTULOS_PIXEL[v].nome,
                    }))}
                  />
                  <Campo
                    rotulo="Identificador"
                    value={p.id}
                    placeholder={ROTULOS_PIXEL[p.provedor].exemplo}
                    onChange={(e) => alterar(i, { id: e.target.value })}
                    ajuda={erro ?? ROTULOS_PIXEL[p.provedor].ajuda}
                  />
                </div>

                {p.provedor === 'google_ads' && (
                  <Campo
                    rotulo="Rótulo da conversão"
                    value={p.rotuloConversao ?? ''}
                    placeholder="AbCdEfGhIj"
                    onChange={(e) => alterar(i, { rotuloConversao: e.target.value })}
                    ajuda="A parte depois da barra em AW-123456789/AbCdEfGh. Sem ela o Ads mede visita e nunca mede lead."
                  />
                )}

                <div className="flex justify-end">
                  <Botao
                    variante="secundario"
                    tamanho="sm"
                    onClick={() => onAlterar(pixels.filter((_, j) => j !== i))}
                  >
                    Remover
                  </Botao>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      <Botao
        variante="secundario"
        tamanho="sm"
        onClick={() =>
          onAlterar([...pixels, { provedor: 'meta', id: '', requerConsentimento: true }])
        }
      >
        + Pixel só desta página
      </Botao>
      <p className="text-xs text-slate-400">
        Nenhum deles carrega antes de a pessoa aceitar na barra de cookies (LGPD, art. 7º, I).
      </p>
    </div>
  )
}
